import { CurrencyPipe } from '@angular/common';
import { Component, computed, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject } from 'rxjs';
import { NotifyService } from '../../../service/notify.service';
import { PosthogService } from '../../../service/posthog.service';
import { TheftReportService } from '../../../service/theft-report.service';
import { UserLoginService } from '../../../service/user-login.service';
import { GetTheftReportsOpts, RequestAccessQueryParams, TheftReport, TheftReportError, UserProfile } from '../../../types';
import { linkedQueryParam } from 'ngxtension/linked-query-param';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { TheftReportDatagridComponent } from '../../components/theft-report-datagrid/theft-report-datagrid.component';
import { getTimezoneAbbreviation } from '../../../date.utils';

@Component({
  selector: 'app-theft-report',
  templateUrl: './theft-report.component.html',
  styleUrls: ['./theft-report.component.scss'],
  standalone: false,
})
export class TheftReportComponent implements OnInit {
  public auth = inject(AuthService);
  public userService = inject(UserLoginService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private theftReportService = inject(TheftReportService);
  private posthogService = inject(PosthogService);
  private notifyService = inject(NotifyService);
  private currencyPipe = inject(CurrencyPipe);
  public theftReportDatagrid = viewChild(TheftReportDatagridComponent);

  // Initialize TheftReports[] data, to avoid *ngIf (prevents problems with MatTable)
  public theftReports = new BehaviorSubject<TheftReport[]>([]);

  public loadingData = signal<boolean>(true);
  public dataLoaded = signal<boolean>(false);
  public reportDate = signal<string>('');
  public dateRange = signal<string>('');
  public locationName = signal<string>('');
  public locationId = signal<number | undefined>(undefined);
  public locationTimezone = signal<string | undefined>(undefined);
  public branchName = signal<string>('');
  public branchId = signal<number | undefined>(undefined);
  public assetRecoveryEnabled = signal<boolean>(false);
  public errorMessage = signal<string>('');
  public hideCurrency = signal<boolean>(true);
  public shareKey = input<string>();
  private apiKey = signal<string | undefined>(undefined);

  public pageSubheader = computed(() => (this.dataLoaded() ? `${this.branchName()}: ${this.locationName()} (${this.reportDate()})` : ''));
  public timezoneSubheader = computed(() => {
    if (!this.dataLoaded()) return '';

    const timezoneAbbreviation = getTimezoneAbbreviation(this.locationTimezone());
    return timezoneAbbreviation ? `Times shown in ${timezoneAbbreviation}` : "This location's timezone is not updated on the Panoptyc App";
  });

  private caseIdQueryParam = injectQueryParams('case_id');
  private dateSharedFromFilter = signal<string | undefined>(undefined);
  private dateSharedToFilter = signal<string | undefined>(undefined);
  private incidentDateFromFilter = linkedQueryParam<string | undefined>('incident_date_from');
  private incidentDateToFilter = linkedQueryParam<string | undefined>('incident_date_to');
  public completedCount = signal<number>(0);

  // variables to compute guest user
  private userProfile = signal<UserProfile | undefined>(undefined);
  readonly pageReviewOutcomeIds = linkedQueryParam<string[]>('ids', {
    parse: (value) => (value ? value.split(',') : []),
    stringify: (value) => value.join(','),
  });

  readonly pageCaseIds = linkedQueryParam<string[]>('caseIds', {
    parse: (value) => (value ? value.split(',') : []),
    stringify: (value) => value.join(','),
  });

  ngOnInit() {
    this.posthogService.capture('view_theft_report', { shareKey: this.shareKey() });

    this.auth.isAuthenticated$.subscribe((isAuthenticated) => {
      this.route.queryParams.subscribe((params) => {
        this.apiKey.set(params.api_key);

        if (!this.apiKey() && isAuthenticated) {
          // we use this to tell us later, if the user is a "branch user" or "guest user"
          this.userService.getMe().subscribe(this.userProfile.set);
        }

        this.dateSharedFromFilter.set(params.date_shared_from);
        this.dateSharedToFilter.set(params.date_shared_to);
      });
    });

    this.refresh();
  }

  /**
   * Refresh the theft report data. This will redirect to the login
   * flow if the authentication fails (status 401)
   */
  refresh() {
    this.loadingData.set(true);

    const opts: GetTheftReportsOpts = {
      case_id: this.caseIdQueryParam() ?? undefined,
      case_ids: this.pageCaseIds(),
      date_shared_from: this.dateSharedFromFilter(),
      date_shared_to: this.dateSharedToFilter(),
      incident_date_from: this.incidentDateFromFilter(),
      incident_date_to: this.incidentDateToFilter(),
    };

    const apiKey = this.apiKey();

    this.theftReportService.getTheftReport(this.shareKey(), opts, apiKey).subscribe({
      next: (response) => {
        const reviewOutcomeIds = this.pageReviewOutcomeIds();
        const reviewOutcomeIdsSet = reviewOutcomeIds.length > 0 ? new Set(reviewOutcomeIds) : null;
        const caseId = this.caseIdQueryParam();
        const reports = response.theft_reports.reduce((acc, r) => {
          if (reviewOutcomeIdsSet && !reviewOutcomeIdsSet.has(r.id)) return acc;
          if (caseId && r.case_id !== caseId) return acc;

          acc.push({
            ...r,
            item_cancellation_value: isNaN(+r.item_cancellation_value)
              ? r.item_cancellation_value
              : this.currencyPipe.transform(r.item_cancellation_value),
          });
          return acc;
        }, []);
        this.theftReports.next(reports);
        this.locationName.set(response.location_name);
        this.locationId.set(response.location_id);
        this.locationTimezone.set(response.location_timezone);
        this.completedCount.set(response.completed_count);
        this.branchName.set(response.branch_name);
        this.branchId.set(response.branch_id);
        this.assetRecoveryEnabled.set(response.asset_recovery_enabled);
        this.hideCurrency.set(response.hide_currency);
        this.dateRange.set(response.date_range);
        this.reportDate.set(this.dateRange()?.split(' - ')[1] ?? 'All incidents, All time');
        this.loadingData.set(false);
        this.dataLoaded.set(true);
      },
      error: (err) => this.handleError(err, opts),
    });
  }

  handleError(httpError, opts?: GetTheftReportsOpts) {
    try {
      switch (httpError.status) {
        case 401:
          this.loadingData.set(false);
          this.dataLoaded.set(false);
          this.errorMessage.set('Session has expired, re-authenticating...');
          this.reauthenticate();
          break;
        case 403: {
          let branchName = '';
          let locationName = '';
          let branchId = 0;
          try {
            const payload = JSON.parse(httpError.error.error) as TheftReportError;
            branchName = payload.branch_name;
            locationName = payload.location_name;
            branchId = payload.branch_id;
          } catch (e) {
            console.error("Failed to parse error's JSON payload", e);
          }

          const queryParams: RequestAccessQueryParams = {
            type: 'theft-report',
            displayName: `${branchName} - ${locationName}`,
            theftReportKey: this.shareKey(),
            branchId: branchId,
            prev_url: this.router.url,
            ...(opts || {}),
          };

          this.router.navigate(['/shared/request-access'], { queryParams });
          break;
        }
        case 404:
          this.router.navigate(['/404']);
          break;
        default:
          this.errorMessage.set(`The error occured while loading the data. We apologize for the inconvenience.
            If the error persists, please contact Panoptyc support team.`);
          this.loadingData.set(false);
          this.dataLoaded.set(false);
      }
    } catch (e) {
      console.error(e);
    }
  }

  reauthenticate() {
    this.route.data.subscribe(() => this.userService.navigateToLogin());
  }

  onMergeCases(review_ids: string[]) {
    this.theftReportService.mergeCases({ review_ids }).subscribe(
      () => this.notifyService.notify('Successfully merged cases', 'Dismiss'),
      (error) => this.notifyService.notify(`Failed to merge cases: ${error.error?.error || error.message}`),
      // Refresh no matter what (e.g. what if the merge partially succeeded)
      () => this.refresh(),
    );
  }

  onMarkNoTheft(review_ids: string[]) {
    this.theftReportService.markNoTheft({ review_ids }).subscribe(
      () => this.notifyService.notify('Successfully marked as Not a Theft', 'Dismiss'),
      (error) => this.notifyService.notify(`Failed to mark as not a theft: ${error.error?.error || error.message}`),
      // Refresh no matter what (e.g. what if the update partially succeeded)
      () => this.refresh(),
    );
  }
}
